/* eslint-disable @typescript-eslint/member-delimiter-style */
import { Component, Inject, OnInit, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { truncate } from 'lodash';
import { MmDialogConfig, MmDialogService, MmTooltipOptions, MmTooltipTrigger } from '@metromarkets/components';

import {
  NUMBER_0,
  NUMBER_10,
  NUMBER_16,
  NUMBER_22,
  NUMBER_5,
  NUMBER_500,
  NUMBER_600,
  NUMBER_8,
} from '~shared/constants';
import { CreateDigitalAccountDialogComponent } from '~shared/components/create-digital-account-dialog/create-digital-account-dialog.component';

import { AuthorizationHttpService } from '../../services/authorization-http.service';
import { EmployeeService } from '../../services/employee.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { isPlatformBrowser } from '@angular/common';
import { FF_FORCE_DIGITAL_IDENTITY_CREATION } from '../../constants/authorization.constant';
import { MediaObserver } from '@angular/flex-layout';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { AccountStatusEnum } from '~shared/constants/account-info.constant';
import { IEmployee } from '~shared/models';
import { AccountInfoService } from '~app/core/modules/identity/services/account-info.service';

export interface Customer {
  fullName: string;
  metroCard: string;
  phone?: string;
  email?: string;
  companyName: string;
  formattedAddress: string;
  personCdmId: string;
  businessCdmId?: string;
  status: number;
  impersonationEnabled: boolean;
}

const DEBOUNCE_TIME = NUMBER_500;

const tooltipOptionsDesktop: MmTooltipOptions = {
  showDelay: NUMBER_600,
  trigger: MmTooltipTrigger.hover,
};

const tooltipOptionsMobile: MmTooltipOptions = {
  showDelay: NUMBER_0,
  trigger: MmTooltipTrigger.click,
};

export const formatCardNumber = (cardNumber?: string): string => {
  // Check for valid cardNumber length
  if (cardNumber?.length !== NUMBER_22) {
    return '';
  }

  // Extract homeStore and customerNumber
  const homeStore = cardNumber.slice(NUMBER_5, NUMBER_8);
  const customerNumber = cardNumber.slice(NUMBER_8, NUMBER_16);

  // Remove leading zeros
  const customerNumberNoLeadingZeros = parseInt(customerNumber, NUMBER_10).toString();
  const homeStoreNoLeadingZeros = parseInt(homeStore, NUMBER_10).toString();

  // Return formatted cardNumber
  return `${homeStoreNoLeadingZeros}/${customerNumberNoLeadingZeros}`;
};

@Component({
  selector: 'app-customers-list',
  templateUrl: './customers-list.component.html',
  styleUrls: ['./customers-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CustomersListComponent implements OnInit {
  searchFormElement: FormGroup;
  dataLoading = false;
  isSearchTriggered = false;
  isImpersonationAllowed = true;
  totalCount = NUMBER_0;
  isSortable = false;
  error = null;
  displayedColumns = [
    'fullName',
    'metroCard',
    'companyName',
    'formattedAddress',
    'email',
    'hasDigitalIdentity',
    'Action',
  ];
  dataSource: Observable<Customer[]>;
  debouncedSearchTerm: Observable<string>;
  onSubmit = this.triggerInvokedSearchTerm;
  truncate = truncate;
  isDesktop = true;
  tooltipOptions = tooltipOptionsDesktop;
  searchTerm = '';
  formatCardNumber = formatCardNumber;
  private invokedSearchTerm = new Subject<string>();
  private forceDigitalIdentityCreation = false;
  private unsubscribe$ = new Subject<boolean>();

  constructor(
    private accountInfoService: AccountInfoService,
    private readonly formBuilder: FormBuilder,
    private readonly authorizationHttpService: AuthorizationHttpService,
    private readonly mmDialogService: MmDialogService,
    private readonly employeeService: EmployeeService,
    private readonly activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    private readonly mediaObserver: MediaObserver,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this.accountInfoService.getEmployeeAccountInfo().subscribe((info: IEmployee) => {
      this.isImpersonationAllowed = info?.canImpersonateCustomer ?? true;
    });
    this.searchFormElement = this.formBuilder.group({
      searchTerm: [{ value: '', disabled: !this.isImpersonationAllowed }, [Validators.required]],
    });
    this.debouncedSearchTerm = this.searchFormElement.controls.searchTerm.valueChanges.pipe(
      debounceTime(DEBOUNCE_TIME)
    );
    this.dataSource = this.searchCustomers();

    if (isPlatformBrowser(this.platformId)) {
      this.activeFeaturesConfigcatService
        .isFeatureEnabledReadyForCurrentCountry$(FF_FORCE_DIGITAL_IDENTITY_CREATION)
        .subscribe((enabled) => {
          this.forceDigitalIdentityCreation = enabled;
        });
      this.mediaObserver
        .asObservable()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          this.isDesktop = this.mediaObserver.isActive(MEDIA_ALISES.desktop);
          this.tooltipOptions = this.isDesktop ? tooltipOptionsDesktop : tooltipOptionsMobile;
        });
    }
  }

  ngOnInit(): void {}

  public ngOnDestroy(): void {
    this.unsubscribe$.next(false);
    this.unsubscribe$.complete();
  }

  triggerInvokedSearchTerm() {
    this.invokedSearchTerm.next(this.searchFormElement.controls.searchTerm.value ?? '');
  }

  searchCustomers(enableDebouncedSearch = true) {
    const searchTermSource = enableDebouncedSearch
      ? merge(this.invokedSearchTerm, this.debouncedSearchTerm)
      : this.invokedSearchTerm;

    return searchTermSource.pipe(
      map((query = '') => query.trim()),
      distinctUntilChanged(),
      switchMap((query) => {
        this.searchTerm = query;
        this.isSearchTriggered = !!query;

        if (this.isSearchTriggered) {
          this.dataLoading = true;
          const filterMetroEmployees = localStorage?.getItem('filterMetroEmployees') ?? '1';
          return this.authorizationHttpService.searchCustomers({ query, filterMetroEmployees }).pipe(
            takeUntil(this.dataSource), // Cancels previous pending request in favour of the current one
            map((data) => {
              this.resetTable(data.count);
              return data.items;
            })
          );
        }
        this.resetTable();
        return of([] as Customer[]);
      }),
      catchError((error, caught) => {
        this.resetTable(NUMBER_0, error);
        return merge(of([] as Customer[]), caught);
      })
    );
  }

  createDigitalAccount(customerCdmId: string, businessCdmId?: string, email?: string, metroCard = null) {
    const dialogConfig = new MmDialogConfig();
    dialogConfig.data = {
      id: 2,
      title: 'Create digital account',
      customerCdmId,
      businessCdmId,
      email,
      metroCard
    };

    this.mmDialogService.open(CreateDigitalAccountDialogComponent, dialogConfig);
  }

  impersonateCustomer(customerCdmId: string, businessCdmId?: string, hasDigitalIdentity = false, email = '', metroCard = null) {
    if (this.forceDigitalIdentityCreation && hasDigitalIdentity === false) {
      this.createDigitalAccount(customerCdmId, businessCdmId, email, metroCard);
    } else {
      this.employeeService.impersonateCustomer(customerCdmId, businessCdmId, email, hasDigitalIdentity).subscribe();
    }
  }

  getStatus(item: Customer): AccountStatusEnum {
    return item.impersonationEnabled ? AccountStatusEnum.validated : AccountStatusEnum.blocked;
  }

  private resetTable = (count = NUMBER_0, error = null) => {
    this.totalCount = count;
    this.dataLoading = false;
    this.error = error;
  };

  logout(e?: Event) {
    e?.preventDefault();

    this.employeeService.handleEmployeeLogout().subscribe();
  }

  onFocus() {}
  onBlur() {}
  onKeydown(event) {}
}
